<script setup>

import { contact_information, store as storeConfig, store } from "~app_root/public/tv/app.config";
import MainLayout from "@/Layouts/MainLayout.vue";
import { Head, Link, usePage } from '@inertiajs/vue3';
import RelatedProductsGallery from '@/Components/Product/RelatedProductsGallery.vue';
import FavoriteButton from '@/Components/Product/ProductComponents/FavoriteButton.vue'
import ProductMedia from '@/Components/Product/ProductMedia.vue';
import ProductDetails from '@/Components/Product/ProductDetails.vue';
import ImageGallery from "@/Components/ImageGallery.vue";
import BuyButton from '@/Components/Product/ProductComponents/BuyButton.vue';
import QuantityControl from "@/Components/ECart/Reusable/QuantityControl.vue";
import ReviewsSummary from "@/Components/Product/ReviewsSummary.vue";
import Reviews from "@/Components/Product/Reviews.vue";
import NewReview from "@/Components/Product/NewReview.vue";
import WhatsAppLink from "@/Components/WhatsAppLink.vue";
import ProductIntegratedPromotion from '@/Components/Product/ProductIntegratedPromotion.vue';
import { useCartItem } from "@/Composables/useCartItem";
import Breadcrumbs from "@/Components/Misc/Breadcrumbs.vue";
import { computed, nextTick, onMounted, ref } from 'vue';

const props = defineProps({
	product: {
		type: Object,
		required: true
	},
	breadcrumbs: {
		type: Array,
		required: true
	},
	productIntegratedPromotions: {
		type: Array,
		default: () => []
	}
});

const page = usePage();

const cartItem = useCartItem(props.product.uuid);

// Data properties converted to refs
const quantity = ref(1);
const uicontrol = ref({
	gallery: {
		show: false,
		key: 0
	},
	ready: false,
	quantity_input: {
		show: true
	}
});

// Computed property
const quantity_computed = computed({
	get() {
		return quantity.value;
	},
	set(newVal) {
		// Prevent entering of anything that is not a number && prevent numbers smaller than 1
		if (newVal === '' || newVal < 1) {
			// Hide the number input and show it back on next tick
			uicontrol.value.quantity_input.show = false;
			nextTick(() => {
				uicontrol.value.quantity_input.show = true;
			});
			return;
		}

		newVal = store.limitMaxQuantity(newVal);
		quantity.value = newVal;
	}
});

// Methods
const registerFbPixelViewContent = () => {
	if (typeof fbq !== 'undefined') {
		fbq('track', 'ViewContent', {
			content_ids: [props.product.sku],
			content_type: 'product',
			content_name: props.product.translation.title,
			value: props.product.price.price_new,
			currency: page.props.store.currency_code
		});
	}
};

const changeGalleryKey = (key) => {
	uicontrol.value.gallery.key = key;
};

const addProductToCart = () => {
	ECart.addItem(props.product.uuid, quantity.value);
};

// Mounted hook
onMounted(() => {
	registerFbPixelViewContent();
	uicontrol.value.ready = true;
});


const badgeGroups = ref({});

function populateBadgeGroups() {

	if (!props.product.badges?.length) return false;

	const groups = {};

	props.product.badges.forEach((badge) => {
		if (!groups[badge.group]) {
			groups[badge.group] = [];
		}
		groups[badge.group].push(badge);
	});

	badgeGroups.value = groups; // Properly update the ref

}

onMounted(() => {
	populateBadgeGroups();
});


</script>

<template>

	<MainLayout>

		<Head>
			<title head-key="title">{{ product.translation.meta.title || product.translation.title }}</title>
			<meta
				:content="product.translation.meta.description || product.translation.meta.title || product.translation.title"
				head-key="description" name="description" />
		</Head>

		<Breadcrumbs :elements="breadcrumbs" />

		<div v-if="uicontrol.ready" class="container product-page mt-2">

			<div class="row">
				<ImageGallery v-if="uicontrol.gallery.show" :images="product.images"
					:initial_key="uicontrol.gallery.key" @change_gallery_key="changeGalleryKey($event)"
					@close_image_gallery="uicontrol.gallery.show = false"></ImageGallery>
			</div>

			<div class="row mt-3">

				<!-- Main media gallery -->
				<ProductMedia :product="product" class="col-12 col-lg-6" @change_gallery_key="changeGalleryKey($event)"
					@open_gallery="uicontrol.gallery.show = true"></ProductMedia>

				<div class="col-12 col-lg-6 product-info ps-4 pe-4 ps-lg-2 pe-lg-0 pt-0 pt-sm-4 pt-md-5 pt-lg-0">
					<h1 class="page-title">
						{{ product.translation.title }}
					</h1>

					<div class="d-flex justify-content-start gap-3 mt-4">
						<FavoriteButton :product_id="product.id" />

						<ReviewsSummary :average_rating="product.reviews_summary?.average_rating || 0" :large="true"
							:linkto="'#reviews'" :total_reviews="product.reviews_summary?.total_reviews || 0" />
					</div>

					<template v-if="product.price.price_new > 0 || storeConfig.product_page.allow_price_zero_purchases">

						<div class="d-flex justify-content-between justify-content-xl-start py-4">

							<div class="product-prices me-xl-5">
								<span class="price font-semibold me-2">
									{{ product.price.price_new.toFixed(2) }} {{ $page.props.store.currency_symbol ||
										$page.props.store.currency_code
									}}
								</span>
								<span v-if="product.price.price_old > product.price.price_new"
									class="old-price font-semibold text-danger">
									{{ product.price.price_old.toFixed(2)
									}} {{ $page.props.store.currency_symbol || $page.props.store.currency_code }}
								</span>
							</div>


							<div class="product-add-to-cart">
								<template v-if="product.stock">

									<div v-if="storeConfig.add_to_cart_quantity_controls && cartItem.quantity.value"
										class="d-flex">
										<i class="lnr icon-cart cart quantity-controls-icon-cart"></i>
										<QuantityControl v-model="cartItem.quantity.value"
											:max="storeConfig.product_max_quantity" />
									</div>

									<BuyButton v-else :button_text="'add_to_cart'" :product_uuid="product.uuid"
										:quantity="1" :show_popup="storeConfig.add_to_cart_confirmation_popup"
										:visitUrlAfterAddToCart="route('catchall', page.url)"
										class="highlighted p-2 flex-grow-1 my-2 my-sm-0">
									</BuyButton>

								</template>

								<h4 v-else>{{ __('item_not_in_stock') }}</h4>
							</div>
						</div>
					</template>

					<div class="detailItemContainer fullWidthMobile mt-1 mt-lg-5">
						<div class="detailItem" v-for="(badge, index) in badgeGroups.tag" :style="{
							'background-color' : badge.background_color,
							'color' : badge.color
						}">
							<img height="20" :src="'images_generic/icons/' + badge.icon" class="icon">
							<span class="ms-2">{{badge.title}}</span>
						</div>

						<div class="detailItem">
							<template v-if="product.stock">
								<i class="lnr icon-check me-2 text-success"></i> <span> {{ __('in_stock') }}</span>
							</template>
							<template v-else>
								<i class="lnr icon-cross text-danger me-2"></i> <span> {{ __('out_of_stock') }}</span>
							</template>
						</div>

						<div class="detailItem">
							<Link v-if="storeConfig.product_page.show_discreet_shipping" :href="'/livrare-discreta'"
								class="d-flex justify-content-start">
								<svg height="20" viewBox="0 0 448 512" xmlns="http://www.w3.org/2000/svg">
									<path
										d="M50.7 58.5L0 160H208V32H93.7C75.5 32 58.9 42.3 50.7 58.5zM240 160H448L397.3 58.5C389.1 42.3 372.5 32 354.3 32H240V160zm208 32H0V416c0 35.3 28.7 64 64 64H384c35.3 0 64-28.7 64-64V192z" />
								</svg>
								<span class="ms-2">Livrare discreta</span>
							</Link>
						</div>

						<div v-if="product.price.price_new > 0 && storeConfig.product_page.show_shipping_cost" class="detailItem">

							<Link v-if="product.price.price_new < store.free_shipping_threshold"
								:href="'/detalii-livrare'">
								<i class="lnr icon-truck"></i> <span class="ms-2">{{ __('shipping_costs') }}</span>
							</Link>

							<span v-else class="text-success font-semibold">
								<i class="lnr icon-truck"></i>
								<span class="ms-1">
									{{ __('shipping.free_shipping_for_this_product') }}
								</span>
							</span>
						</div>

					</div>

					<div class="detailItemContainer">

						<div class="detailItem">
							<i class="lnr icon-barcode me-2"></i> <span class="font-semibold">{{ __('product_code')
							}}</span> <span class="ms-1">{{ product.sku }}</span>

							<span v-if="product.eans_array.length" class="ms-2"><span class="font-semibold">EAN</span>
								{{
									product.eans_array[0]
								}}</span>
						</div>

						<WhatsAppLink v-if="contact_information.show_whatsapp_button" :floating="false" class="detailItem whatsapp" />

						<a v-if="storeConfig.product_page.show_phone_numbers"
							v-for="(phone, pKey) in contact_information.phones" :href="'tel:' + phone.value"
							class="text-decoration-none d-flex gap-2 detailItem phone">
							<i class="lnr icon-telephone"></i> <span>{{ phone.label }}</span>
						</a>

					</div>

					<div class="d-flex gap-3 mt-5">
						<div class="badges bubble">
							<div v-for="(badge, index) in badgeGroups.bubble" :key="index" :style="{
									'background-color': badge.background_color + ' !important',
								}" class="badge">
								<img :src="'images_generic/icons/' + badge.icon" class="icon">
								<span :style="{ 'color': badge.color + ' !important' }">{{ badge.title }}</span>
							</div>
						</div>
					</div>

					<div v-if="product.related.length" class="mt-4">
						<h3 class="border-bottom-1 pb-2">{{ __('related_products') }}</h3>
						<!--Related products Gallery-->
						<RelatedProductsGallery :items="product.related" class="my-4"></RelatedProductsGallery>
					</div>

				</div>

			</div>

			<!-- Product Integrated Promotions -->
			<ProductIntegratedPromotion class="mt-4" :promotions="productIntegratedPromotions"
				v-if="productIntegratedPromotions.length" />

			<div class="row">
				<ProductDetails :product="product" class="col-12 p-4" />
			</div>

			<div class="row" v-if="product.docs.length">
				<div class="col-12 p-4 mb-3">
					<h3 class="border-bottom-1 pb-2">{{ __('product_docs') }}</h3>
					<ul class="list-unstyled">
						<li v-for="(doc, index) in product.docs" :key="index">
							<a :href="'/storage/product_docs/' + doc.name" target="_blank" class="text-decoration-none">
								<svg v-if="doc.mime_type === 'application/pdf'" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512" width="24" height="24" class="me-1"><path d="M0 64C0 28.7 28.7 0 64 0L224 0l0 128c0 17.7 14.3 32 32 32l128 0 0 144-208 0c-35.3 0-64 28.7-64 64l0 144-48 0c-35.3 0-64-28.7-64-64L0 64zm384 64l-128 0L256 0 384 128zM176 352l32 0c30.9 0 56 25.1 56 56s-25.1 56-56 56l-16 0 0 32c0 8.8-7.2 16-16 16s-16-7.2-16-16l0-48 0-80c0-8.8 7.2-16 16-16zm32 80c13.3 0 24-10.7 24-24s-10.7-24-24-24l-16 0 0 48 16 0zm96-80l32 0c26.5 0 48 21.5 48 48l0 64c0 26.5-21.5 48-48 48l-32 0c-8.8 0-16-7.2-16-16l0-128c0-8.8 7.2-16 16-16zm32 128c8.8 0 16-7.2 16-16l0-64c0-8.8-7.2-16-16-16l-16 0 0 96 16 0zm80-112c0-8.8 7.2-16 16-16l48 0c8.8 0 16 7.2 16 16s-7.2 16-16 16l-32 0 0 32 32 0c8.8 0 16 7.2 16 16s-7.2 16-16 16l-32 0 0 48c0 8.8-7.2 16-16 16s-16-7.2-16-16l0-64 0-64z" fill="#F40F02"/></svg>
								<i class="lnr icon-file"></i> <span class="font-semibold text-decoration-underline">{{ doc.name }}</span>
							</a>
						</li>
					</ul>
				</div>
			</div>

			<div class="row">
				<Reviews :reviews="product.reviews" />
				<NewReview :product="product" />
			</div>
		</div>
	</MainLayout>

</template>